import type { TypographyProps } from '@heroui/react';

/**
 * `render` functions for HeroUI Typography on the details page. Typography
 * picks its element from its `type` (`p` for body text, `h3` for an `h3`), and
 * these keep its type, colour and weight on the element the markup needs
 * instead: a heading one level off its size, a term or a definition, a table
 * cell, a list item, or a `span` where a `p` isn't allowed (inside a button). The Text
 * underneath passes every prop on, so what Typography is given still lands.
 */
type TypographyRender = NonNullable<TypographyProps['render']>;

export const asSpan: TypographyRender = ({ children, ...domProps }) => (
    <span {...domProps}>{children}</span>
);

export const asHeading2: TypographyRender = ({ children, ...domProps }) => (
    <h2 {...domProps}>{children}</h2>
);

export const asHeading4: TypographyRender = ({ children, ...domProps }) => (
    <h4 {...domProps}>{children}</h4>
);

export const asListItem: TypographyRender = ({ children, ...domProps }) => (
    <li {...domProps}>{children}</li>
);

export const asTerm: TypographyRender = ({ children, ...domProps }) => (
    <dt {...domProps}>{children}</dt>
);

export const asDefinition: TypographyRender = ({ children, ...domProps }) => (
    <dd {...domProps}>{children}</dd>
);

/** A column header, `scope="col"` so a screen reader names each cell's column. */
export const asColumnHeader: TypographyRender = ({ children, ...domProps }) => (
    <th {...domProps} scope="col">
        {children}
    </th>
);

export const asCell: TypographyRender = ({ children, ...domProps }) => (
    <td {...domProps}>{children}</td>
);
